import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { IconButton } from "@/components/common/IconButton";
import {
	captionStyle,
	cellStyle,
	pageTitleStyle,
	sectionHeadingStyle,
} from "@/components/infra/infra-styles";
import {
	PerformanceTrendChart,
	type TrendAnnotations,
} from "@/components/infra/PerformanceTrendChart";
import {
	createPerformanceAnnotation,
	deletePerformanceAnnotation,
	getDbQueryStats,
	getDbQueryTagTrend,
	getDbQueryTrend,
	getHttpEndpointErrors,
	getHttpEndpointStats,
	getHttpEndpointTrend,
	getPerformanceAnnotations,
	type DbQuerySort,
	type DbQueryStats,
	type DbQueryTrend,
	type HttpEndpointError,
	type HttpEndpointStat,
	type HttpEndpointStats,
	type HttpEndpointTrendPoint,
	type PerformanceAnnotation,
} from "@/lib/api";
import { formatDateTime } from "@/lib/format-datetime";
import {
	PERFORMANCE_PERIODS,
	dbQueriesCsv,
	dbQueryTrendCsv,
	endpointTrendCsv,
	endpointsCsv,
	formatMeanRows,
	formatMs,
	matchesPin,
	meanTrend,
	queryIdsInEffect,
	queryVersionSpans,
	sortEndpoints,
	splitPinnedQueries,
	totalTrend,
	trendCaption,
	type EndpointSort,
	type Periods,
	type QueryVersionSpan,
	type RankedQuery,
	type Trend,
} from "@/lib/infra-performance";
import {
	readPerformancePins,
	writePerformancePins,
	type PerformancePins,
	type PinnedQuery,
} from "@/lib/performance-pins";
import { ACTION_LINK_CLASS } from "@/lib/link-styles";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useToastStore } from "@/stores/useToastStore";

const REFRESH_INTERVAL_MS = 60_000;
const QUERY_LIMITS = [10, 25, 50, 100];
const ENDPOINTS_SHOWN_COLLAPSED = 10;

const headerCellStyle: CSSProperties = { ...cellStyle, fontWeight: 500 };
const numberCellStyle: CSSProperties = captionStyle({
	...cellStyle,
	textAlign: "right",
	whiteSpace: "nowrap",
	fontVariantNumeric: "tabular-nums",
});
const numberHeaderStyle: CSSProperties = { ...headerCellStyle, textAlign: "right" };
/** 数値の右に矢印を置くための余白。見出しにも同じだけ空けて、数値と見出しの右端を揃える。 */
const TREND_GUTTER_PX = 22;
const trendCellStyle: CSSProperties = { ...numberCellStyle, paddingRight: TREND_GUTTER_PX };
const PIN_COLUMN_WIDTH = 28;
const pinCellStyle: CSSProperties = { ...cellStyle, padding: "3px 4px 0 0" };
/** ピン留めした行と、それ以外の上位の行の境目。 */
const groupStartStyle: CSSProperties = { borderTop: "2px solid var(--line-1)" };

/**
 * 管理ページの「パフォーマンス」タブ (/admin/performance、SuperAdmin のみ)。指定期間に重かった DB クエリと、
 * エンドポイントごとの応答時間を見せる。
 */
export function PerformancePage() {
	const [hours, setHours] = useState<number>(24);
	const [reloadKey, setReloadKey] = useState(0);
	const pins = usePerformancePins();

	useEffect(() => {
		const timer = window.setInterval(() => setReloadKey((k) => k + 1), REFRESH_INTERVAL_MS);
		return () => window.clearInterval(timer);
	}, []);

	return (
		<div
			data-testid="performance-page"
			style={{ padding: 32, height: "100%", overflowY: "auto", background: "var(--surface-1)" }}
		>
			<h2 style={pageTitleStyle}>パフォーマンス</h2>
			<p style={captionStyle({ margin: "6px 0 16px" })}>
				期間内に重かった DB クエリと、エンドポイントごとの応答時間です (全レプリカの合計)。
				クエリやエンドポイントを押すと推移のグラフを表示します。グラフを押すと、その行のグラフの時刻に目印を付けられます。
				{REFRESH_INTERVAL_MS / 1000} 秒ごとに読み直します。
			</p>

			<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
				<select
					className="ds-select"
					data-testid="performance-period"
					value={hours}
					onChange={(e) => setHours(Number(e.target.value))}
					style={{ width: 160, height: 32 }}
				>
					{PERFORMANCE_PERIODS.map((p) => (
						<option key={p.hours} value={p.hours}>
							{p.label}
						</option>
					))}
				</select>
				<IconButton
					icon="rotate"
					title="パフォーマンスを更新"
					data-testid="performance-refresh"
					size={26}
					onClick={() => setReloadKey((k) => k + 1)}
					style={{ marginLeft: "auto" }}
				/>
			</div>

			<DbQuerySection hours={hours} reloadKey={reloadKey} pins={pins} />
			<EndpointSection hours={hours} reloadKey={reloadKey} pins={pins} />
		</div>
	);
}

/**
 * target の行 (クエリやエンドポイント) の推移のグラフの目印。付けたり消したりしたら読み直す。
 * 読み込みに失敗しても、グラフは目印なしで表示を続ける。
 */
function useTrendAnnotations(target: string, hours: number, reloadKey: number) {
	const [changeKey, setChangeKey] = useState(0);
	const { data, error } = useLoaded<PerformanceAnnotation[]>(
		() => getPerformanceAnnotations({ target, hours }),
		`${target}/${hours}/${reloadKey}/${changeKey}`,
	);
	const value: TrendAnnotations = {
		items: data ?? [],
		onAdd: async (at, text) => {
			await createPerformanceAnnotation({ target, at, text });
			setChangeKey((k) => k + 1);
		},
		onDelete: async (id) => {
			await deletePerformanceAnnotation(id);
			setChangeKey((k) => k + 1);
		},
	};
	return { value, error };
}

/** 監視したいクエリとエンドポイントを、順位に関係なく表の上に並べるためのピン留め。ユーザーごとに localStorage へ残す。 */
function usePerformancePins() {
	const userId = useCurrentUserStore((s) => s.id);
	const [pins, setPins] = useState(() => readPerformancePins(userId));
	const update = (next: PerformancePins) => {
		setPins(next);
		writePerformancePins(userId, next);
	};
	return {
		queries: pins.queries,
		endpoints: pins.endpoints,
		toggleQuery: (query: PinnedQuery) =>
			update({
				...pins,
				queries: pins.queries.some((p) => matchesPin(query, p))
					? pins.queries.filter((p) => !matchesPin(query, p))
					: [...pins.queries, query],
			}),
		toggleEndpoint: (name: string) =>
			update({
				...pins,
				endpoints: pins.endpoints.includes(name)
					? pins.endpoints.filter((p) => p !== name)
					: [...pins.endpoints, name],
			}),
	};
}

type Pins = ReturnType<typeof usePerformancePins>;

/** key が変わるたびに load を呼び直し、最後に成功した結果とエラーを返す。古い呼び出しの結果は捨てる。 */
function useLoaded<T>(load: () => Promise<T>, key: string) {
	const [data, setData] = useState<T | null>(null);
	const [error, setError] = useState<string | null>(null);
	const loadRef = useRef(load);
	loadRef.current = load;

	useEffect(() => {
		let current = true;
		loadRef
			.current()
			.then((result) => {
				if (!current) return;
				setData(result);
				setError(null);
			})
			.catch((err: Error) => {
				if (current) setError(err.message);
			});
		return () => {
			current = false;
		};
	}, [key]);

	return { data, error };
}

function DbQuerySection({
	hours,
	reloadKey,
	pins,
}: {
	hours: number;
	reloadKey: number;
	pins: Pins;
}) {
	const [sort, setSort] = useState<DbQuerySort>("Total");
	const [limit, setLimit] = useState(10);
	const pinnedIds = pins.queries.map((p) => p.queryId);
	const pinnedTags = pins.queries.flatMap((p) => (p.tag === null ? [] : [p.tag]));
	const { data, error } = useLoaded<DbQueryStats>(
		() => getDbQueryStats({ hours, sort, limit, pinned: pinnedIds, pinnedTags }),
		`${hours}/${sort}/${limit}/${pinnedIds.join(",")}/${pinnedTags.join(",")}/${reloadKey}`,
	);
	const queries = data?.available ? data.queries : [];
	const { pinned, others } = splitPinnedQueries(queries, limit, pins.queries);
	const pinnedAbsent = pins.queries.filter((p) => !queries.some((q) => matchesPin(q, p)));
	const shown = [...pinned, ...others].map((r) => r.query);

	return (
		<section data-testid="performance-db-queries" style={{ marginBottom: 32 }}>
			<SectionHeader
				title="DB クエリ"
				since={data?.available ? data.since : null}
				controls={
					<>
						<CopyCsvButton
							csv={shown.length > 0 ? dbQueriesCsv(shown) : null}
							testId="performance-db-queries-copy-csv"
						/>
						<select
							className="ds-select"
							data-testid="performance-query-limit"
							value={limit}
							onChange={(e) => setLimit(Number(e.target.value))}
							style={{ width: 100, height: 28 }}
						>
							{QUERY_LIMITS.map((n) => (
								<option key={n} value={n}>
									上位 {n}
								</option>
							))}
						</select>
					</>
				}
			/>
			<LoadState error={error} loading={data === null} />
			{data !== null && !data.available && (
				<p style={captionStyle({ margin: 0 })}>
					DB で pg_stat_statements が読み込まれていないため表示できません。
				</p>
			)}
			{data?.available &&
				(data.queries.length === 0 && pins.queries.length === 0 ? (
					<p style={captionStyle({ margin: 0 })}>期間内に実行されたクエリはありません。</p>
				) : (
					<DbQueryTable
						pinned={pinned}
						pinnedAbsent={pinnedAbsent}
						others={others}
						onTogglePin={pins.toggleQuery}
						periods={data}
						sort={sort}
						onSort={setSort}
						hours={hours}
						reloadKey={reloadKey}
					/>
				))}
		</section>
	);
}

function DbQueryTable({
	pinned,
	pinnedAbsent,
	others,
	onTogglePin,
	periods,
	sort,
	onSort,
	hours,
	reloadKey,
}: {
	pinned: RankedQuery[];
	/** ピン留めしたが期間内に実行されなかったクエリ。 */
	pinnedAbsent: PinnedQuery[];
	others: RankedQuery[];
	onTogglePin: (query: PinnedQuery) => void;
	periods: Periods;
	sort: DbQuerySort;
	onSort: (sort: DbQuerySort) => void;
	hours: number;
	reloadKey: number;
}) {
	const header = (value: DbQuerySort, label: string, width: number, title?: string) => (
		<SortHeader
			label={label}
			active={sort === value}
			onClick={() => onSort(value)}
			width={width}
			title={title}
			withTrend={value === "Mean" || value === "Total"}
			testId={`performance-query-sort-${value}`}
		/>
	);

	const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
	const toggle = (id: string) =>
		setExpanded((current) => {
			const next = new Set(current);
			if (!next.delete(id)) next.add(id);
			return next;
		});

	const row = ({ query: q, rank }: RankedQuery, isPinned: boolean, groupStart = false) => {
		const current = { count: q.calls, totalMs: q.totalMs };
		const previous = { count: q.previousCalls, totalMs: q.previousTotalMs };
		const style = (base: CSSProperties) => (groupStart ? { ...base, ...groupStartStyle } : base);
		return (
			<Fragment key={q.queryId}>
				<tr data-testid={`performance-query-row-${q.queryId}`}>
					<td style={style(pinCellStyle)}>
						<PinButton
							pinned={isPinned}
							onClick={() => onTogglePin({ queryId: q.queryId, query: q.query, tag: q.tag })}
						/>
					</td>
					<td style={style(captionStyle(cellStyle))}>{rank ?? "—"}</td>
					<td style={style(cellStyle)}>
						<QueryText
							text={q.query}
							expanded={expanded.has(q.queryId)}
							onToggle={() => toggle(q.queryId)}
						/>
					</td>
					<td style={style(numberCellStyle)}>{q.calls.toLocaleString()}</td>
					<td style={style(trendCellStyle)}>
						<WithTrend kind="mean" trend={meanTrend(current, previous)}>
							{formatMs(q.meanMs)}
						</WithTrend>
					</td>
					<td style={style(numberCellStyle)}>{formatMs(q.stddevMs)}</td>
					<td style={style(numberCellStyle)}>{formatMs(q.maxMs)}</td>
					<td style={style(trendCellStyle)}>
						<WithTrend kind="total" trend={totalTrend(current, previous, periods)}>
							{formatMs(q.totalMs)}
						</WithTrend>
					</td>
					<td style={style(numberCellStyle)}>{formatMeanRows(q.meanRows)}</td>
					<td style={style(numberCellStyle)}>
						{q.cacheHitRatio === null ? "—" : `${(q.cacheHitRatio * 100).toFixed(1)}%`}
					</td>
				</tr>
				{trendRow(q.queryId, q.tag)}
			</Fragment>
		);
	};

	const trendRow = (queryId: string, tag: string | null) =>
		expanded.has(queryId) && (
			<tr>
				<td colSpan={10} style={{ ...cellStyle, borderTop: "none" }}>
					<DbQueryTrendPanel queryId={queryId} tag={tag} hours={hours} reloadKey={reloadKey} />
				</td>
			</tr>
		);

	return (
		<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
			<thead>
				<tr style={captionStyle({ textAlign: "left" })}>
					<th style={{ ...headerCellStyle, width: PIN_COLUMN_WIDTH }} />
					<th style={{ ...headerCellStyle, width: 32 }}>#</th>
					<th style={headerCellStyle}>クエリ</th>
					{header("Calls", "実行回数", 88)}
					{header("Mean", "平均", 94)}
					{header("Stddev", "標準偏差", 88)}
					{header("Max", "最大", 80, "期間に関係なく、DB の統計をリセットしてからの最大です")}
					{header("Total", "合計時間", 110)}
					{header("MeanRows", "平均行数", 80)}
					<th style={{ ...numberHeaderStyle, width: 96 }}>キャッシュ率</th>
				</tr>
			</thead>
			<tbody>
				{pinned.map((r) => row(r, true))}
				{pinnedAbsent.map((p) => (
					<Fragment key={p.queryId}>
						<tr data-testid={`performance-query-row-${p.queryId}`}>
							<td style={pinCellStyle}>
								<PinButton pinned onClick={() => onTogglePin(p)} />
							</td>
							<td style={captionStyle(cellStyle)}>—</td>
							<td style={cellStyle}>
								<QueryText
									text={p.query}
									expanded={expanded.has(p.queryId)}
									onToggle={() => toggle(p.queryId)}
								/>
							</td>
							<td colSpan={7} style={captionStyle(cellStyle)}>
								期間内に実行されていません
							</td>
						</tr>
						{trendRow(p.queryId, p.tag)}
					</Fragment>
				))}
				{others.map((r, i) => row(r, false, i === 0 && pinned.length + pinnedAbsent.length > 0))}
			</tbody>
		</table>
	);
}

function QueryText({
	text,
	expanded,
	onToggle,
}: {
	text: string;
	expanded: boolean;
	onToggle: () => void;
}) {
	return (
		// button の文字はドラッグで選択できないため div にする。選択した直後のクリックでは開閉しない。
		<div
			className="select-text"
			onClick={() => {
				if (window.getSelection()?.isCollapsed !== false) onToggle();
			}}
			data-tooltip={expanded ? "クリックで折りたたむ" : "クリックで全文と推移を表示"}
			style={{
				display: "-webkit-box",
				WebkitBoxOrient: "vertical",
				WebkitLineClamp: expanded ? "unset" : 2,
				overflow: "hidden",
				whiteSpace: expanded ? "pre-wrap" : "normal",
				wordBreak: "break-all",
				fontFamily: "var(--font-mono)",
				fontSize: "var(--text-caption)",
				lineHeight: "var(--leading-normal)",
				color: "var(--fg-1)",
				cursor: "pointer",
			}}
		>
			{text}
		</div>
	);
}

function PinButton({ pinned, onClick }: { pinned: boolean; onClick: () => void }) {
	return (
		<IconButton
			icon="pin"
			iconSize={14}
			size={22}
			data-tooltip={pinned ? "ピン留めを外す" : "ピン留めして、順位に関係なく上に表示する"}
			data-testid={pinned ? "performance-unpin" : "performance-pin"}
			color={pinned ? "var(--accent)" : "var(--fg-3)"}
			hoverColor={pinned ? "var(--accent)" : "var(--fg-1)"}
			onClick={onClick}
		/>
	);
}

function EndpointSection({
	hours,
	reloadKey,
	pins,
}: {
	hours: number;
	reloadKey: number;
	pins: Pins;
}) {
	const [sort, setSort] = useState<EndpointSort>("total");
	const [showAll, setShowAll] = useState(false);
	const { data, error } = useLoaded<HttpEndpointStats>(
		() => getHttpEndpointStats(hours),
		`${hours}/${reloadKey}`,
	);
	const sorted = data === null ? [] : sortEndpoints(data.endpoints, sort);
	const isPinned = (e: HttpEndpointStat) => pins.endpoints.includes(endpointName(e));
	const pinned = sorted.filter(isPinned);
	const others = sorted.filter((e) => !isPinned(e));
	const pinnedAbsent = pins.endpoints.filter(
		(name) => !sorted.some((e) => endpointName(e) === name),
	);

	return (
		<section data-testid="performance-endpoints">
			<SectionHeader
				title="エンドポイント"
				since={data?.since ?? null}
				controls={
					<CopyCsvButton
						csv={sorted.length > 0 ? endpointsCsv([...pinned, ...others]) : null}
						testId="performance-endpoints-copy-csv"
					/>
				}
			/>
			<LoadState error={error} loading={data === null} />
			{data !== null &&
				(sorted.length === 0 && pins.endpoints.length === 0 ? (
					<p style={captionStyle({ margin: 0 })}>期間内のリクエストはありません。</p>
				) : (
					<>
						<EndpointTable
							pinned={pinned}
							pinnedAbsent={pinnedAbsent}
							others={showAll ? others : others.slice(0, ENDPOINTS_SHOWN_COLLAPSED)}
							onTogglePin={pins.toggleEndpoint}
							periods={data}
							sort={sort}
							onSort={setSort}
							hours={hours}
							reloadKey={reloadKey}
						/>
						{others.length > ENDPOINTS_SHOWN_COLLAPSED && (
							<button
								type="button"
								data-testid="performance-endpoints-show-all"
								onClick={() => setShowAll((v) => !v)}
								className={ACTION_LINK_CLASS}
								style={{ marginTop: 8 }}
							>
								{showAll
									? `上位 ${ENDPOINTS_SHOWN_COLLAPSED} 件だけ表示`
									: `すべて表示 (${others.length} 件)`}
							</button>
						)}
					</>
				))}
		</section>
	);
}

function endpointName(e: HttpEndpointStat): string {
	return `${e.method} ${e.route}`;
}

function EndpointTable({
	pinned,
	pinnedAbsent,
	others,
	onTogglePin,
	periods,
	sort,
	onSort,
	hours,
	reloadKey,
}: {
	pinned: HttpEndpointStat[];
	/** ピン留めしたが期間内にリクエストが無かったエンドポイントの名前。 */
	pinnedAbsent: string[];
	others: HttpEndpointStat[];
	onTogglePin: (name: string) => void;
	periods: Periods;
	sort: EndpointSort;
	onSort: (sort: EndpointSort) => void;
	hours: number;
	reloadKey: number;
}) {
	const [errorsShownFor, setErrorsShownFor] = useState<string | null>(null);
	const [trendShownFor, setTrendShownFor] = useState<ReadonlySet<string>>(new Set());
	const toggleTrend = (name: string) =>
		setTrendShownFor((current) => {
			const next = new Set(current);
			if (!next.delete(name)) next.add(name);
			return next;
		});
	const trendRow = (name: string) =>
		trendShownFor.has(name) && (
			<tr>
				<td colSpan={10} style={{ ...cellStyle, borderTop: "none" }}>
					<EndpointTrendPanel name={name} hours={hours} reloadKey={reloadKey} />
				</td>
			</tr>
		);
	const header = (value: EndpointSort, label: string, width: number) => (
		<SortHeader
			label={label}
			active={sort === value}
			onClick={() => onSort(value)}
			width={width}
			withTrend={value === "mean" || value === "total"}
			testId={`performance-endpoint-sort-${value}`}
		/>
	);

	const row = (e: HttpEndpointStat, isPinned: boolean, groupStart = false) => {
		const name = endpointName(e);
		const current = { count: e.requestCount, totalMs: e.totalMs };
		const previous = { count: e.previousRequestCount, totalMs: e.previousTotalMs };
		const style = (base: CSSProperties) => (groupStart ? { ...base, ...groupStartStyle } : base);
		return (
			<Fragment key={name}>
				<tr data-testid={`performance-endpoint-row-${name}`}>
					<td style={style(pinCellStyle)}>
						<PinButton pinned={isPinned} onClick={() => onTogglePin(name)} />
					</td>
					<td style={style(cellStyle)}>
						<EndpointNameText name={name} onClick={() => toggleTrend(name)} />
					</td>
					<td style={style(numberCellStyle)}>{e.requestCount.toLocaleString()}</td>
					<td
						style={style({
							...numberCellStyle,
							color: e.errorCount > 0 ? "var(--danger)" : numberCellStyle.color,
						})}
					>
						{e.errorCount > 0 ? (
							<button
								type="button"
								data-testid={`performance-endpoint-errors-toggle-${name}`}
								onClick={() => setErrorsShownFor((current) => (current === name ? null : name))}
								data-tooltip={errorsShownFor === name ? "エラーを閉じる" : "エラーの内容を表示"}
								style={{
									padding: 0,
									border: "none",
									background: "none",
									font: "inherit",
									color: "inherit",
									textDecoration: "underline",
									cursor: "pointer",
								}}
							>
								{e.errorCount.toLocaleString()}
							</button>
						) : (
							e.errorCount.toLocaleString()
						)}
					</td>
					<td style={style(trendCellStyle)}>
						<WithTrend kind="mean" trend={meanTrend(current, previous)}>
							{formatMs(e.meanMs)}
						</WithTrend>
					</td>
					<td style={style(numberCellStyle)}>{formatMs(e.p50Ms)}</td>
					<td style={style(numberCellStyle)}>{formatMs(e.p95Ms)}</td>
					<td style={style(numberCellStyle)}>{formatMs(e.p99Ms)}</td>
					<td style={style(numberCellStyle)}>{formatMs(e.maxMs)}</td>
					<td style={style(trendCellStyle)}>
						<WithTrend kind="total" trend={totalTrend(current, previous, periods)}>
							{formatMs(e.totalMs)}
						</WithTrend>
					</td>
				</tr>
				{trendRow(name)}
				{errorsShownFor === name && (
					<tr>
						<td colSpan={10} style={{ ...cellStyle, borderTop: "none" }}>
							<EndpointErrors
								method={e.method}
								route={e.route}
								hours={hours}
								reloadKey={reloadKey}
							/>
						</td>
					</tr>
				)}
			</Fragment>
		);
	};

	return (
		<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
			<thead>
				<tr style={captionStyle({ textAlign: "left" })}>
					<th style={{ ...headerCellStyle, width: PIN_COLUMN_WIDTH }} />
					<th style={headerCellStyle}>エンドポイント</th>
					{header("count", "リクエスト数", 104)}
					{header("errors", "5xx", 72)}
					{header("mean", "平均", 94)}
					{header("p50", "p50", 80)}
					{header("p95", "p95", 80)}
					{header("p99", "p99", 80)}
					{header("max", "最大", 80)}
					{header("total", "合計時間", 110)}
				</tr>
			</thead>
			<tbody>
				{pinned.map((e) => row(e, true))}
				{pinnedAbsent.map((name) => (
					<Fragment key={name}>
						<tr data-testid={`performance-endpoint-row-${name}`}>
							<td style={pinCellStyle}>
								<PinButton pinned onClick={() => onTogglePin(name)} />
							</td>
							<td style={cellStyle}>
								<EndpointNameText name={name} onClick={() => toggleTrend(name)} />
							</td>
							<td colSpan={8} style={captionStyle(cellStyle)}>
								期間内のリクエストはありません
							</td>
						</tr>
						{trendRow(name)}
					</Fragment>
				))}
				{others.map((e, i) => row(e, false, i === 0 && pinned.length + pinnedAbsent.length > 0))}
			</tbody>
		</table>
	);
}

/** `${method} ${route}` をメソッドとルートに分ける。 */
function splitEndpointName(name: string): { method: string; route: string } {
	const [method, ...route] = name.split(" ");
	return { method, route: route.join(" ") };
}

/** メソッドを薄く見せる。押すと推移を開閉する (選択した直後のクリックでは開閉しない)。 */
function EndpointNameText({ name, onClick }: { name: string; onClick: () => void }) {
	const { method, route } = splitEndpointName(name);
	return (
		<div
			className="select-text"
			data-testid={`performance-endpoint-name-${name}`}
			onClick={() => {
				if (window.getSelection()?.isCollapsed !== false) onClick();
			}}
			title={name}
			data-tooltip-if-truncated
			style={{
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				fontFamily: "var(--font-mono)",
				fontSize: "var(--text-caption)",
				color: "var(--fg-1)",
				cursor: "pointer",
			}}
		>
			<span style={{ color: "var(--fg-3)" }}>{method}</span> {route}
		</div>
	);
}

const formatCount = (n: number) => Math.round(n).toLocaleString();

function TrendPanel({
	testId,
	hours,
	error,
	loading,
	csv,
	children,
}: {
	testId: string;
	hours: number;
	error: string | null;
	loading: boolean;
	csv: string | null;
	children: ReactNode;
}) {
	return (
		<div
			data-testid={testId}
			style={{ padding: 8, borderRadius: "var(--radius-3)", background: "var(--surface-2)" }}
		>
			<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
				<p style={captionStyle({ margin: 0 })}>{trendCaption(hours)}</p>
				<div style={{ marginLeft: "auto" }}>
					<CopyCsvButton csv={csv} testId={`${testId}-copy-csv`} />
				</div>
			</div>
			<LoadState error={error} loading={loading} />
			{!loading && <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>{children}</div>}
		</div>
	);
}

/** タグの付いたクエリは、同じタグの全てのクエリ ID (書き換える前後) を合算した推移を見せる。 */
function DbQueryTrendPanel({
	queryId,
	tag,
	hours,
	reloadKey,
}: {
	queryId: string;
	tag: string | null;
	hours: number;
	reloadKey: number;
}) {
	const { data, error } = useLoaded<DbQueryTrend>(
		() => (tag === null ? getDbQueryTrend({ queryId, hours }) : getDbQueryTagTrend({ tag, hours })),
		`${hours}/${reloadKey}`,
	);
	const annotations = useTrendAnnotations(
		tag === null ? `query:${queryId}` : `query-tag:${tag}`,
		hours,
		reloadKey,
	);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const spans = data?.available ? queryVersionSpans(data.points, data.versions) : [];
	const selected = spans.find((v) => v.queryId === selectedId) ?? spans.at(-1);
	const versions =
		data?.available && spans.length > 1 && selected !== undefined
			? {
					ids: queryIdsInEffect(data.points),
					labels: new Map(spans.map((v) => [v.queryId, v.label])),
					selected: selected.queryId,
					onSelect: setSelectedId,
				}
			: undefined;
	return (
		<TrendPanel
			testId="performance-query-trend"
			hours={hours}
			error={error ?? annotations.error}
			loading={data === null}
			csv={data?.available ? dbQueryTrendCsv(data.points) : null}
		>
			{data !== null &&
				(data.available ? (
					<>
						<PerformanceTrendChart
							title="平均時間"
							points={data.points}
							series={[{ label: "平均", color: "var(--accent)", value: (p) => p.meanMs }]}
							format={formatMs}
							testId="performance-query-trend-mean"
							annotations={annotations.value}
							versions={versions}
						/>
						<PerformanceTrendChart
							title="実行回数"
							points={data.points}
							series={[{ label: "実行回数", color: "var(--accent)", value: (p) => p.calls }]}
							format={formatCount}
							testId="performance-query-trend-calls"
							annotations={annotations.value}
							versions={versions}
						/>
						{versions !== undefined && selected !== undefined && (
							<QueryVersions spans={spans} selected={selected} onSelect={setSelectedId} />
						)}
					</>
				) : (
					<p style={captionStyle({ margin: 0 })}>
						DB で pg_stat_statements が読み込まれていないため表示できません。
					</p>
				))}
		</TrendPanel>
	);
}

/** タグのクエリを書き換えた版の一覧と、選んだ版の文面。グラフの背景を押しても版を選べる。 */
function QueryVersions({
	spans,
	selected,
	onSelect,
}: {
	spans: QueryVersionSpan[];
	selected: QueryVersionSpan;
	onSelect: (queryId: string) => void;
}) {
	const day = (iso: string | null) => (iso === null ? "" : formatDateTime(iso));
	return (
		<div data-testid="performance-query-versions" style={{ flex: "1 1 100%", minWidth: 0 }}>
			<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
				{spans.map((v) => {
					const active = v.queryId === selected.queryId;
					return (
						<button
							key={v.queryId}
							type="button"
							data-testid={`performance-query-version-${v.queryId}`}
							onClick={() => onSelect(v.queryId)}
							style={captionStyle({
								padding: "2px 8px",
								border: `1px solid ${active ? "var(--accent-line)" : "var(--line-1)"}`,
								borderRadius: "var(--radius-3)",
								background: active ? "var(--accent-subtle)" : "none",
								color: active ? "var(--accent)" : "var(--fg-2)",
								cursor: "pointer",
							})}
						>
							{v.label} ({day(v.from)} 〜 {day(v.to)})
						</button>
					);
				})}
			</div>
			<pre
				className="select-text"
				data-testid="performance-query-version-text"
				style={{
					margin: 0,
					whiteSpace: "pre-wrap",
					wordBreak: "break-all",
					fontFamily: "var(--font-mono)",
					fontSize: "var(--text-caption)",
					lineHeight: "var(--leading-normal)",
					color: "var(--fg-1)",
				}}
			>
				{selected.query ?? "文面が残っていません。"}
			</pre>
		</div>
	);
}

function EndpointTrendPanel({
	name,
	hours,
	reloadKey,
}: {
	name: string;
	hours: number;
	reloadKey: number;
}) {
	const { data, error } = useLoaded<HttpEndpointTrendPoint[]>(
		() => getHttpEndpointTrend({ ...splitEndpointName(name), hours }),
		`${hours}/${reloadKey}`,
	);
	const annotations = useTrendAnnotations(`endpoint:${name}`, hours, reloadKey);
	return (
		<TrendPanel
			testId="performance-endpoint-trend"
			hours={hours}
			error={error ?? annotations.error}
			loading={data === null}
			csv={data === null ? null : endpointTrendCsv(data)}
		>
			{data !== null && (
				<>
					<PerformanceTrendChart
						title="応答時間"
						points={data}
						series={[
							{ label: "平均", color: "var(--accent)", value: (p) => p.meanMs },
							{ label: "p95", color: "var(--warning)", value: (p) => p.p95Ms },
						]}
						format={formatMs}
						testId="performance-endpoint-trend-latency"
						annotations={annotations.value}
					/>
					<PerformanceTrendChart
						title="リクエスト数"
						points={data}
						series={[
							{ label: "リクエスト数", color: "var(--accent)", value: (p) => p.requestCount },
							{ label: "5xx", color: "var(--danger)", value: (p) => p.errorCount },
						]}
						format={formatCount}
						testId="performance-endpoint-trend-requests"
						annotations={annotations.value}
					/>
				</>
			)}
		</TrendPanel>
	);
}

/** エンドポイントが期間内に返した 5xx の見本。行を開くと例外の全文が読める。 */
function EndpointErrors({
	method,
	route,
	hours,
	reloadKey,
}: {
	method: string;
	route: string;
	hours: number;
	reloadKey: number;
}) {
	const { data, error } = useLoaded<HttpEndpointError[]>(
		() => getHttpEndpointErrors({ method, route, hours }),
		`${hours}/${reloadKey}`,
	);

	return (
		<div
			data-testid="performance-endpoint-errors"
			style={{ padding: 8, borderRadius: "var(--radius-3)", background: "var(--surface-2)" }}
		>
			<LoadState error={error} loading={data === null} />
			{data !== null &&
				(data.length === 0 ? (
					<p style={captionStyle({ margin: 0 })}>
						期間内に記録されたエラーはありません (記録を始める前のエラーは残っていません)。
					</p>
				) : (
					<>
						<p style={captionStyle({ margin: "0 0 4px" })}>
							新しい順に最大 50 件。1 時間あたり 50 件を超えた分は件数だけ数えています。
						</p>
						{data.map((err) => (
							<EndpointErrorItem key={err.id} error={err} />
						))}
					</>
				))}
		</div>
	);
}

function EndpointErrorItem({ error }: { error: HttpEndpointError }) {
	const monoStyle: CSSProperties = {
		fontFamily: "var(--font-mono)",
		fontSize: "var(--text-caption)",
		lineHeight: "var(--leading-normal)",
		color: "var(--fg-1)",
	};
	return (
		<details data-testid={`performance-endpoint-error-${error.id}`}>
			<summary
				style={captionStyle({
					display: "flex",
					gap: 8,
					padding: "2px 0",
					color: "var(--fg-1)",
					cursor: "pointer",
				})}
			>
				<span style={{ whiteSpace: "nowrap", color: "var(--fg-3)" }}>
					{formatDateTime(error.occurredAt)}
				</span>
				<span style={{ color: "var(--danger)" }}>{error.statusCode}</span>
				<span
					style={{
						minWidth: 0,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{error.exceptionType === null
						? "例外なし"
						: `${error.exceptionType}: ${error.message ?? ""}`}
				</span>
			</summary>
			<div className="select-text" style={{ ...monoStyle, padding: "4px 0 8px 16px" }}>
				<div>パス: {error.path}</div>
				<div>traceId: {error.traceId}</div>
				{error.detail !== null && (
					<pre
						style={{
							...monoStyle,
							margin: "4px 0 0",
							whiteSpace: "pre-wrap",
							wordBreak: "break-all",
						}}
					>
						{error.detail}
					</pre>
				)}
			</div>
		</details>
	);
}

const TRENDS = {
	mean: {
		label: "平均",
		up: { arrow: "↑", color: "var(--danger)" },
		down: { arrow: "↓", color: "var(--success)" },
		steady: { arrow: "→", color: "var(--fg-3)" },
	},
	// 合計が増えるのは利用が増えただけのこともあるため、平均の悪化 (赤) と色を分ける。
	total: {
		label: "合計 (同じ長さに換算)",
		up: { arrow: "↑", color: "var(--warning)" },
		down: { arrow: "↓", color: "var(--success)" },
		steady: { arrow: "→", color: "var(--fg-3)" },
	},
} as const;

/** 数値の右の余白 (TREND_GUTTER_PX) に、直前の同じ長さの期間と比べた変化の矢印を付ける。 */
function WithTrend({
	kind,
	trend,
	children,
}: {
	kind: keyof typeof TRENDS;
	trend: Trend | null;
	children: ReactNode;
}) {
	if (trend === null) return children;
	const { label, [trend.direction]: look } = TRENDS[kind];
	const percent = Math.round(trend.change * 100);
	return (
		<span style={{ position: "relative" }}>
			<span
				data-testid={`performance-trend-${kind}-${trend.direction}`}
				title={`前の期間の${label} ${formatMs(trend.previousMs)} から ${percent > 0 ? "+" : ""}${percent}%`}
				// → と ↑↓ は字幅が違うため、同じ幅の枠の中央に置いて揃える。
				style={{
					position: "absolute",
					left: "calc(100% + 4px)",
					width: "1em",
					textAlign: "center",
					color: look.color,
				}}
			>
				{look.arrow}
			</span>
			{children}
		</span>
	);
}

/** 押すとその列の大きい順に並べる見出し。並べている列には ▼ を付ける。 */
function SortHeader({
	label,
	active,
	onClick,
	width,
	title,
	withTrend = false,
	testId,
}: {
	label: string;
	active: boolean;
	onClick: () => void;
	width: number;
	title?: string;
	withTrend?: boolean;
	testId: string;
}) {
	return (
		<th
			style={{ ...numberHeaderStyle, width, ...(withTrend && { paddingRight: TREND_GUTTER_PX }) }}
			title={title}
		>
			<button
				type="button"
				data-testid={testId}
				onClick={onClick}
				style={{
					padding: 0,
					border: "none",
					background: "none",
					font: "inherit",
					fontWeight: active ? 600 : 500,
					color: active ? "var(--fg-1)" : "inherit",
					whiteSpace: "nowrap",
					cursor: "pointer",
				}}
			>
				{active && "▼ "}
				{label}
			</button>
		</th>
	);
}

/** 表やグラフの全行を CSV でクリップボードに入れる。 */
function CopyCsvButton({ csv, testId }: { csv: string | null; testId: string }) {
	const copy = async () => {
		if (csv === null) return;
		try {
			await navigator.clipboard.writeText(csv);
		} catch (err) {
			useToastStore.getState().show({ type: "error", message: "CSV のコピーに失敗しました。" });
			throw err;
		}
		useToastStore.getState().show({ type: "success", message: "CSV としてコピーしました。" });
	};
	return (
		<IconButton
			icon="copy"
			title="CSV としてコピー"
			data-testid={testId}
			size={26}
			disabled={csv === null}
			onClick={() => {
				copy().catch((err: unknown) => console.error("CSV のコピーに失敗しました。", err));
			}}
		/>
	);
}

function SectionHeader({
	title,
	since,
	controls,
}: {
	title: string;
	since: string | null;
	controls?: ReactNode;
}) {
	return (
		<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
			<h3 style={{ ...sectionHeadingStyle, margin: 0 }}>{title}</h3>
			{since !== null && <span style={captionStyle()}>{formatDateTime(since)} 以降</span>}
			<div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>{controls}</div>
		</div>
	);
}

function LoadState({ error, loading }: { error: string | null; loading: boolean }) {
	if (error !== null)
		return (
			<p style={{ margin: "0 0 8px", fontSize: "var(--text-ui)", color: "var(--danger)" }}>
				{error}
			</p>
		);
	if (loading) return <p style={captionStyle({ margin: 0 })}>読み込み中…</p>;
	return null;
}
